import {
  DASHBOARD_QUERY_ROOT_KEY,
  DASHBOARD_SKIP_CACHE,
} from "@/core/constants/dashboard.constants";
import { keysToCamelCase } from "@/core/helpers/dashboard-api-adapter";
import type {
  DashboardCacheVersions,
  DashboardWidgetName,
} from "@/core/interfaces/Dashboard";
import { dashboardQueryClient } from "@/core/plugins/vue-query";
import store from "@/store";

function widgetFromQueryKey(
  queryKey: readonly unknown[]
): DashboardWidgetName | null {
  const widget = queryKey[1];
  return typeof widget === "string" ? (widget as DashboardWidgetName) : null;
}

function getStoredVersions(): DashboardCacheVersions {
  const module = (
    store.state as {
      DashboardModule?: { versions?: DashboardCacheVersions };
    }
  ).DashboardModule;
  return module?.versions ?? { global: 0, map: 0 };
}

function setStoredVersions(versions: DashboardCacheVersions): void {
  store.commit("DashboardModule/setVersions", versions);
}

export function invalidateDashboardWidget(widget: DashboardWidgetName): void {
  dashboardQueryClient.invalidateQueries({
    queryKey: [DASHBOARD_QUERY_ROOT_KEY, widget],
  });
}

export function invalidateDashboardWidgets(
  widgets: DashboardWidgetName[]
): void {
  widgets.forEach((widget) => invalidateDashboardWidget(widget));
}

/** Invalidate all dashboard widgets except cache-versions + meta polls. */
export function invalidateAllDashboardWidgets(): void {
  dashboardQueryClient.invalidateQueries({
    queryKey: [DASHBOARD_QUERY_ROOT_KEY],
    predicate: (query) => {
      const widget = widgetFromQueryKey(query.queryKey);
      return widget !== "cache-versions" && widget !== "meta";
    },
  });
}

/**
 * Selectively invalidate widgets based on cache version deltas.
 * Does not touch the cache-versions poller query.
 */
export function applyDashboardVersionChanges(
  previous: DashboardCacheVersions,
  next: DashboardCacheVersions
): void {
  if (DASHBOARD_SKIP_CACHE) {
    return;
  }

  if (next.global !== previous.global) {
    invalidateAllDashboardWidgets();
    return;
  }

  if (next.summary != null && next.summary !== previous.summary) {
    invalidateDashboardWidgets(["summary", "system-summary"]);
  }

  if (next.movement != null && next.movement !== previous.movement) {
    invalidateDashboardWidget("inventory-movement");
  }

  if (next.tickets != null && next.tickets !== previous.tickets) {
    invalidateDashboardWidgets(["ticket-statuses", "summary"]);
  }

  if (next.alerts != null && next.alerts !== previous.alerts) {
    invalidateDashboardWidgets(["alerts", "summary"]);
  }

  if (next.topEntities != null && next.topEntities !== previous.topEntities) {
    invalidateDashboardWidget("top-entities");
  }

  if (next.map !== previous.map) {
    invalidateDashboardWidget("map");
  }

  if (next.activities != null && next.activities !== previous.activities) {
    invalidateDashboardWidget("recent-activities");
  }

  if (next.unit != null && next.unit !== previous.unit) {
    invalidateAllDashboardWidgets();
  }

  if (next.warehouse != null && next.warehouse !== previous.warehouse) {
    invalidateAllDashboardWidgets();
  }
}

function isDashboardCacheVersions(
  value: unknown
): value is DashboardCacheVersions {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as DashboardCacheVersions).global === "number" &&
    typeof (value as DashboardCacheVersions).map === "number"
  );
}

/**
 * Normalize `extra.dashboard_versions` (snake or camel) into DashboardCacheVersions.
 */
export function pickDashboardVersions(payload: unknown): DashboardCacheVersions | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const root = payload as Record<string, unknown>;
  const extra =
    root.extra && typeof root.extra === "object"
      ? (root.extra as Record<string, unknown>)
      : root;

  const raw =
    extra.dashboard_versions ??
    extra.dashboardVersions ??
    root.dashboard_versions ??
    root.dashboardVersions;

  if (!raw || typeof raw !== "object") {
    return null;
  }

  const normalized = keysToCamelCase(raw) as DashboardCacheVersions;
  return isDashboardCacheVersions(normalized) ? normalized : null;
}

/**
 * After a mutation that returns `dashboard_versions`, update the local version
 * store and invalidate only affected widgets — no need to wait for the next poll.
 */
export function applyDashboardVersionsFromResponse(payload: unknown): boolean {
  if (DASHBOARD_SKIP_CACHE) {
    return false;
  }

  const next = pickDashboardVersions(payload);
  if (!next) {
    return false;
  }

  const previous = { ...getStoredVersions() };
  const changed =
    next.global !== previous.global ||
    next.map !== previous.map ||
    next.unit !== previous.unit ||
    next.warehouse !== previous.warehouse ||
    next.activities !== previous.activities ||
    next.summary !== previous.summary ||
    next.movement !== previous.movement ||
    next.tickets !== previous.tickets ||
    next.alerts !== previous.alerts ||
    next.topEntities !== previous.topEntities;

  if (!changed) {
    return false;
  }

  setStoredVersions(next);
  applyDashboardVersionChanges(previous, next);
  return true;
}
